import { Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoadingComponent } from 'src/app/components/loading/loading.component';
import { BreadCrumbItem, KENDO_NAVIGATION  } from '@progress/kendo-angular-navigation';
import { KENDO_CHARTS } from '@progress/kendo-angular-charts';
import { ApplicationApiService, NewRoadmapRequestDto, RegionDashboardDto, RoadmapSummaryDto, RoadmapWithLastPlannerConditionsDto, TargetConditionDto } from 'src/app/services/application-api.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { faQuestionCircle, faCheckCircle, faTimesCircle, faArrowAltCircleRight, faChartBar, faFileAlt } from '@fortawesome/free-regular-svg-icons';
import { faDraftingCompass, faCheck, faAngleDoubleRight, faExclamationCircle, faFileExport, faUserCheck, faUserAltSlash, faStream } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ConstantInfoVariables } from 'src/app/types/constants';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatSortModule } from '@angular/material/sort';
import { DecimalPipe, DatePipe } from '@angular/common';
import { forkJoin } from 'rxjs/internal/observable/forkJoin';
import { MatDialog } from '@angular/material/dialog';
import { NewRoadmapComponent } from './new-roadmap/new-roadmap.component';
import { throwDialogContentAlreadyAttachedError } from '@angular/cdk/dialog';

@Component({
  selector: 'app-region-summary',
  imports: [LoadingComponent, KENDO_NAVIGATION, KENDO_CHARTS, MatCardModule, MatTabsModule, FontAwesomeModule, MatTooltipModule, MatCheckboxModule, FormsModule, MatInputModule,
    MatButtonModule, MatTableModule, MatSortModule, DecimalPipe, DatePipe],
  templateUrl: './region-summary.component.html',
  styleUrl: './region-summary.component.scss'
})
export class RegionSummaryComponent {
  // Inject dependencies
  private readonly apiService = inject(ApplicationApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);

  // Signals for component state
  readonly isLoading = signal(false);
  readonly isCreatingNewRoadmap = signal(false);
  readonly breadcrumbs = signal<BreadCrumbItem[]>([
    { text: "Region Map", title: "Region Map" },
    { text: "Region:", title: "Region" }
  ]);
  readonly region = signal<RegionDashboardDto | null>(null);
  readonly showOutsideLSR = signal(true);
  readonly chartData = signal<any[]>([]);
  readonly lastPlannerGoals = signal<TargetConditionDto[]>([]);
  readonly dataSource = signal(new MatTableDataSource<RoadmapSummaryDto>());
  readonly dataSourceLPS = signal(new MatTableDataSource<RoadmapWithLastPlannerConditionsDto>());
  readonly displayedColumnsLPS = signal<string[]>([]);

  // Computed signal for table title
  readonly tableDisplayTitle = computed(() =>
    this.showOutsideLSR()
      ? 'Active Jobs in and out of Lean Standard Range'
      : 'Active Jobs within Lean Standard Range'
  );

  // Static values (no need for signals)
  readonly infoLsr = ConstantInfoVariables.LeanStandardRange;
  readonly lsrTooltip = ConstantInfoVariables.LeanStandardRange;
  readonly displayedColumns = ['projectName','projectNumber','profitCenterName','revenue','percentComplete','isApproved','roadmapChampion','lastEdited',
                      'plannedCount','achievedCount','statusId','isInLSR'];

  // Icons (static)
  readonly faFileExport = faFileExport;
  readonly faQuestionCircle = faQuestionCircle;
  readonly faCheckCircle = faCheckCircle;
  readonly faTimesCircle = faTimesCircle;
  readonly faDraftingCompass = faDraftingCompass;
  readonly faCheck = faCheck;
  readonly faExclamationCircle = faExclamationCircle;
  readonly faUserCheck = faUserCheck;
  readonly faUserAltSlash = faUserAltSlash;
  readonly faAngleDoubleRight = faAngleDoubleRight;
  readonly faArrowAltCircleRight = faArrowAltCircleRight;
  readonly faChartBar = faChartBar;
  readonly faStream = faStream;
  readonly faFileAlt = faFileAlt;

  constructor() {
    this.loadRegionData();
  }

  private loadRegionData(): void {
    this.isLoading.set(true);

    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe(params => {
      const regionId = Number(params.get('id'));

      forkJoin([
        this.apiService.apiTargetsConditionLastPlannerGoals(),
        this.apiService.apiRegionsDashboard(regionId)
      ]).subscribe({
        next: ([lastPlannerGoals, dashboardData]: [TargetConditionDto[], RegionDashboardDto]) => {
          // Update breadcrumbs
          this.breadcrumbs.set([
            { text: "Region Map", title: "Region Map" },
            { text: 'Region: ' + dashboardData.regionName, title: 'Region: ' + dashboardData.regionName }
          ]);

          // Set data
          this.region.set(dashboardData);
          this.lastPlannerGoals.set(lastPlannerGoals);

          // Setup tables and visuals
          this.setDataSourceLPSAndVisuals();
          this.setDataSourceAndVisuals();
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
        }
      });
    });
  }

  setDataSourceAndVisuals(): void {
    const currentRegion = this.region();
    const ds = this.dataSource();

    if (this.showOutsideLSR()) {
      // Set datasource
      const dataToShow = currentRegion?.roadmaps?.filter(d => d.isInLSR === true || d.isInLSR === false);
      ds.data = dataToShow || [];

      // Chart data
      this.setChartVisual(dataToShow);
    } else {
      // Set datasource
      const dataToShow = currentRegion?.roadmaps?.filter(d => d.isInLSR === true);
      ds.data = dataToShow || [];

      // Chart data
      this.setChartVisual(dataToShow);
    }

    // Trigger signal update for template
    this.dataSource.set(ds);
  }

  setDataSourceLPSAndVisuals(): void {
    const currentRegion = this.region();
    const dsLPS = this.dataSourceLPS();
    dsLPS.data = currentRegion?.roadmapsWithLastPlannerConditions || [];
    this.dataSourceLPS.set(dsLPS);

    const columns = ['projectName', 'projectNumber', 'percentComplete'];

    // For Each Last Planner Goal
    this.lastPlannerGoals().forEach(lpsGoal => {
      if (lpsGoal && lpsGoal.id !== undefined) {
        columns.push(lpsGoal.id.toString());
      }
    });

    this.displayedColumnsLPS.set(columns);
  }

  onShowOutsideLSRChange(value: boolean): void {
    this.showOutsideLSR.set(value);
    this.setDataSourceAndVisuals();
  }

  onCreateRoadmapClick(): void {
    const dialogRef = this.dialog.open(NewRoadmapComponent, {
      width: '800px',
      data: { region: this.region() }
    });

    dialogRef.afterClosed().subscribe((result: NewRoadmapRequestDto | null) => {
      if (result) {
        this.isCreatingNewRoadmap.set(true);

        this.apiService.apiRoadmapPost(result).subscribe({
          next: (newRoadmapId: number) => {
            this.isCreatingNewRoadmap.set(false);
            this.router.navigate(['/roadmap', newRoadmapId]);
          },
          error: (err) => {
            this.isCreatingNewRoadmap.set(false);
            throw err;
          }
        });
      }
    });
  }

  onRoadmapClick(roadmapId: number): void {
    this.router.navigate(['/roadmap', roadmapId]);
  }

  applyFilter(filterValue: string): void {
    const ds = this.dataSource();
    ds.filter = filterValue.trim().toLowerCase();
  }

  applyFilterLPS(filterValue: string): void {
    const dsLPS = this.dataSourceLPS();
    dsLPS.filter = filterValue.trim().toLowerCase();
  }

  onBreadcrumbClick(item: BreadCrumbItem): void {
    if (item.text === "Region Map") {
      this.router.navigate(['/home']);
    }
  }

  private setChartVisual(dataToShow: RoadmapSummaryDto[] | undefined): void {
    this.chartData.set([
      { category: 'Draft', color: '#6969698c', value: dataToShow?.filter(r => r.statusId === 1).length },
      { category: 'Submitted', color: '#4683b496', value: dataToShow?.filter(r => r.statusId === 2).length },
      { category: 'Returned', color: '#ff0000', value: dataToShow?.filter(r => r.statusId === 3).length },
      { category: 'Final', color: '#00b1008c', value: dataToShow?.filter(r => r.statusId === 4).length },
      { category: 'Needs Review', color: '#99cd32a4', value: dataToShow?.filter(r => r.statusId === 5).length },
      { category: 'Dry Run', color: '#dda0dd8c', value: dataToShow?.filter(r => r.statusId === 6).length }
    ]);
  }
}
